import React from 'react';
import {CircularProgress} from "@mui/joy";
import dayjs from "dayjs";

// components
import BaseApi from "../../utils/BaseApi";
import withRouter from "../../utils/withRouter";
import CommonPaginatedTable from "../Common/CommonPaginatedTable";
import retrievePermission from "../../utils/retrievePermission";
import UserSubscriberContext from "../../context/UserSubscriberContext";
import CommonCallBackButton from "../Common/CommonCallBackButton";
import CommonActionModal from "../Common/CommonActionModal";
import CommonForm from "../Common/CommonForm";

// icons
import AddCircleIcon from '@mui/icons-material/AddCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WorkIcon from '@mui/icons-material/Work';


class PositionFormModal extends CommonActionModal {
    async componentDidMount() {
        this.setState({
            buttonIcon: this.props.componentObj?.id ? EditIcon : AddCircleIcon,
            buttonLabel: this.props.componentObj?.id ? 'Редактировать' : 'Добавить',
            ready: true,
        })
    }

    async beforeModalOpen() {
        const api = new BaseApi();
        let fields = await api.get('employee_position/update_form/', {
            fields: JSON.stringify([
                'position',
                'salary',
                'salary_occupation',
                'pay_day_first',
                'pay_day_second',
                'date_start_publish',
                'date_end_publish',
                'staff',
                'model_position',
                'status',
            ])
        });
        
        return {
            modalLabel: this.props.componentObj?.id ? 'Редактирование должности' : 'Создание должности',
            modalIcon: WorkIcon,
            mainComponent: CommonForm,
            mainComponentProps: {
                fields: fields.data,
                object: this.props.componentObj || {},
                processForm: this.processForm.bind(this),
            }
        };
    }

    async processForm(form) {
        const api = new BaseApi();
        const pos = this.props.componentObj;
        let formDataObj = Object.fromEntries(
            form.state.formData.entries()
        );
        
        if (pos) {
            await api.patch(`employee_position/${pos.id}/`, formDataObj);
        } else {
            formDataObj.employee = this.props.employee.id;
            await api.post('employee_position/', formDataObj);
        }
        this.setModalOpenFalse();
        if (this.props.callBack) this.props.callBack();
    }
}


class EmployeePositionList extends React.Component {
    static contextType = UserSubscriberContext

    constructor(props) {
        super(props);
        this.state = {
            updatePermission: 0,
            createPermission: 0,
            deletePermission: 0,
            positions: null,
            pages: null,
            filters: {
                employee: props.employee?.id || null,
            },
            filterFields: null,
            loading: false,
        };
    }

    async componentDidMount() {
        await this.retrievePositions();
        await this.retrievePermission();
    }

    async componentDidUpdate(prevProps) {
        if (prevProps.employee.id !== this.props.employee.id) {
            this.setState({
                filters: {
                    employee: this.props.employee?.id || null,
                }
            });
            await this.retrievePositions();
        }
    }

    async retrievePermission() {
        let updatePermission = await retrievePermission(
            'EmployeePositionViewSet',
            'partial_update',
            this.context.subscriberObject
        );
        let createPermission = await retrievePermission(
            'EmployeePositionViewSet',
            'create',
            this.context.subscriberObject
        );
        let deletePermission = await retrievePermission(
            'EmployeePositionViewSet',
            'destroy',
            this.context.subscriberObject
        );
        await this.setState({
            updatePermission,
            createPermission,
            deletePermission,
        });
    }


    async retrievePositions() {
        this.setState({ loading: true });
        const url = 'employee_position/';
        const api = new BaseApi();
        let params = {
            ...this.state.filters,
        };
        if (this.props.params.pageNumber) {
            params['page'] = this.props.params.pageNumber;
        }
        let response = await api.get(url, params);
        if (response.status === 404) {
            params['page'] = 1;
        }
        response = await api.get(url, params);
        await this.setState({
            positions: response.data.results,
            pages: response.data.total_pages,
            loading: false,
        });
    }

    async deletePosition(id) {
        const api = new BaseApi();
        await api.delete(`employee_position/${id}/`);
        await this.retrievePositions();
    }

    render() {
        if (this.state.loading) return <CircularProgress />;
        if (!this.state.positions || !this.state.pages) {
            return <CircularProgress />;
        }
        return (
            <CommonPaginatedTable
                title="Должности сотрудника"
                url={'employee_position'}
                data={this.state.positions}
                pages={this.state.pages}
                pageNumber={this.props.params.pageNumber !== undefined ? parseInt(this.props.params.pageNumber) : 1}
                callBack={this.retrievePositions.bind(this)}
                extraComponents={
                    this.state.createPermission ? [
                        {
                            component: PositionFormModal,
                            componentProps: {
                                buttonIcon: AddCircleIcon,
                                buttonLabel: 'Добавить должность',
                                flat: false,
                                overlay: false,
                                employee: this.props.employee,
                            },
                            callBack: this.retrievePositions.bind(this),
                        }
                    ] : []
                }
                fields={[
                    {
                        title: 'Дата начала',
                        name: 'date_start_publish',
                        visible: true,
                        preProcess: (value) => (value?dayjs(value).format('DD MMM YYYY'):'Не указано'),
                        edit: {
                            permission: true,
                            fields: ['date_start_publish', ],
                            baseUrl: 'employee_position',
                        },
                    },
                    {
                        title: 'Дата окончания',
                        name: 'date_end_publish',
                        visible: true,
                        preProcess: (value) => (value?dayjs(value).format('DD MMM YYYY'):'Не указано'),
                        edit: {
                            permission: true,
                            fields: ['pay_date', ],
                            baseUrl: 'employee_position',
                        },
                    },
                    {
                        title: 'Должность',
                        name: 'position',
                        visible: true,
                        edit: {
                            permission: true,
                            fields: ['position', ],
                            baseUrl: 'employee_position',
                        },
                    },
                    {
                        title: 'Ставка',
                        name: 'salary',
                        visible: true,
                        preProcess: (v) => v ? `${Number(v).toFixed(2)} ₽` : '',
                        edit: {
                            permission: true,
                            fields: ['salary', ],
                            baseUrl: 'employee_position',
                        },

                    },
                    {
                        title: 'Доля ставки',
                        name: 'salary_occupation',
                        visible: true,
                        preProcess: (v) => `${v}%`,
                        edit: {
                            permission: true,
                            fields: ['salary_occupation', ],
                            baseUrl: 'employee_position',
                        },
                    },
                    {
                        title: 'Штатный',
                        name: 'staff',
                        visible: true,
                        edit: {
                            permission: true,
                            fields: ['staff', ],
                            baseUrl: 'employee_position',
                        },
                    },
                    {
                        title: '',
                        visible: this.state.deletePermission,
                        component: CommonCallBackButton,
                        componentProps: {
                            propsObject: 'componentObj',
                            propsObjectKey: 'id',
                            icon: DeleteIcon,
                            color: 'danger',
                        },
                        width: '40px',
                        callBack: this.deletePosition.bind(this),
                    }
                ]}
            />
        );
    }
}

export default withRouter(EmployeePositionList);
